'use strict';

const libPictProvider = require('pict-provider');

const libAssetDescriptor = require('./AssetDescriptor.js');
// The compiled-in FHWA SNBI vocabulary. Kept ONLY as the fallback for an asset type that has no
// published descriptor yet, so this provider is a no-op change for the Bridge type until its
// descriptor is authored. Delete this require (and the file) once every live type has one.
const libSNBI = require('./SNBI-Dictionary.js');

/**
 * AssetDescriptorProvider — loads, caches and version-resolves asset-type metadata descriptors.
 *
 * Every screen that renders asset metadata asks this provider for an accessor and then stops caring
 * what kind of asset it is looking at. That indirection is the whole point: a bridge's SNBI codes, a
 * culvert's barrel material and a sign's retroreflectivity all arrive through the same surface.
 *
 * ── Which version renders an asset ─────────────────────────────────────────────────────────────
 *
 * A descriptor decides how stored data is DISPLAYED and what the next ingestion EXPLODES, so "which
 * version" is a correctness question, not a preference:
 *
 *   1. The version the asset's data was written against (`PhysicalAsset.ManifestVersion`), when the
 *      asset carries one. Data written under an older vocabulary keeps rendering under that
 *      vocabulary instead of being reinterpreted by a newer one.
 *   2. Otherwise the newest PUBLISHED version. Drafts are invisible here — that is what makes the
 *      editor safe to work in against live data.
 *   3. Otherwise nothing, and the caller gets the fallback.
 *
 * `ManifestVersion` is 0 on every asset today and written by nothing; step 1 exists so that ingestion
 * can start stamping it without a second change here.
 */
class AssetDescriptorProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		/** IDAssetType -> Promise<Array<manifest row>>, so concurrent callers share one fetch. */
		this._manifestsByType = {};
		/** `${IDAssetType}:${Version}` -> normalized descriptor. */
		this._descriptorCache = {};
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	_get(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/** Drop every cached manifest and descriptor — call after saving a descriptor. */
	invalidate(pIDAssetType)
	{
		if (pIDAssetType == null)
		{
			this._manifestsByType = {};
			this._descriptorCache = {};
			return;
		}
		delete this._manifestsByType[Number(pIDAssetType)];
		const tmpPrefix = `${ Number(pIDAssetType) }:`;
		for (const tmpKey of Object.keys(this._descriptorCache))
		{
			if (tmpKey.indexOf(tmpPrefix) === 0) { delete this._descriptorCache[tmpKey]; }
		}
	}

	/**
	 * Every manifest row for an asset type, newest version first. Cached as the PROMISE so a burst of
	 * callers on first paint issues one request, not a dozen.
	 *
	 * @param {Number} pIDAssetType
	 * @return {Promise<Array<Object>>}
	 */
	manifestsForTypeAsync(pIDAssetType)
	{
		const tmpID = Number(pIDAssetType);
		if (!tmpID) { return Promise.resolve([]); }
		if (this._manifestsByType[tmpID]) { return this._manifestsByType[tmpID]; }

		const tmpPromise = this._get(`AssetTypeManifests/FilteredTo/FBV~IDAssetType~EQ~${ tmpID }~FBV~Deleted~EQ~0~FSF~Version~DESC~0/0/100`)
			.then((pRows) => (Array.isArray(pRows) ? pRows.slice().sort((pLeft, pRight) => (Number(pRight.Version) - Number(pLeft.Version))) : []))
			.catch((pError) =>
			{
				// A missing descriptor must degrade to the fallback, never blank a screen.
				this.pict.log.warn(`AssetDescriptor: could not load manifests for asset type ${ tmpID }: ${ pError.message || pError }`);
				delete this._manifestsByType[tmpID];
				return [];
			});

		this._manifestsByType[tmpID] = tmpPromise;
		return tmpPromise;
	}

	/**
	 * Normalize one manifest row into a descriptor, cached by (type, version).
	 *
	 * @param {Number} pIDAssetType
	 * @param {Object} pRow
	 * @return {Object} the normalized descriptor
	 */
	_descriptorFromRow(pIDAssetType, pRow)
	{
		const tmpKey = `${ Number(pIDAssetType) }:${ Number(pRow.Version) || 0 }`;
		if (this._descriptorCache[tmpKey]) { return this._descriptorCache[tmpKey]; }
		// Meadow serves the ManifestJSON column as a parsed `Manifest` object; normalize handles either.
		const tmpManifest = (pRow.Manifest != null) ? pRow.Manifest : pRow.ManifestJSON;
		const tmpDescriptor = libAssetDescriptor.normalizeDescriptor(tmpManifest);
		tmpDescriptor.ManifestVersion = Number(pRow.Version) || 0;
		tmpDescriptor.IDAssetTypeManifest = pRow.IDAssetTypeManifest;
		this._descriptorCache[tmpKey] = tmpDescriptor;
		return tmpDescriptor;
	}

	/**
	 * Resolve the descriptor for an asset type, honoring the asset's own stamped manifest version when
	 * it has one.
	 *
	 * @param {Number} pIDAssetType
	 * @param {Number} [pManifestVersion] - PhysicalAsset.ManifestVersion; 0/absent means "published"
	 * @return {Promise<Object|null>} the normalized descriptor, or null when the type has none
	 */
	async descriptorForTypeAsync(pIDAssetType, pManifestVersion)
	{
		const tmpRows = await this.manifestsForTypeAsync(pIDAssetType);
		if (!tmpRows.length) { return null; }

		const tmpStampedVersion = Number(pManifestVersion) || 0;
		if (tmpStampedVersion > 0)
		{
			const tmpStampedRow = tmpRows.find((pRow) => (Number(pRow.Version) === tmpStampedVersion));
			if (tmpStampedRow)
			{
				const tmpStamped = this._descriptorFromRow(pIDAssetType, tmpStampedRow);
				if (!libAssetDescriptor.isEmpty(tmpStamped)) { return tmpStamped; }
			}
		}

		// Newest published version. Rows are already newest-first.
		for (const tmpRow of tmpRows)
		{
			const tmpDescriptor = this._descriptorFromRow(pIDAssetType, tmpRow);
			if ((tmpDescriptor.Status === 'published') && !libAssetDescriptor.isEmpty(tmpDescriptor))
			{
				return tmpDescriptor;
			}
		}
		return null;
	}

	/**
	 * An accessor for an asset type — the call every renderer makes.
	 *
	 * Falls back to the compiled-in SNBI vocabulary when the type has no published descriptor, so the
	 * Bridge type renders identically before and after its descriptor is authored.
	 *
	 * @param {Number} pIDAssetType
	 * @param {Object} [pOptions] - { SetKey, ManifestVersion }
	 * @return {Promise<Object>} an accessor (never null)
	 */
	async accessorForTypeAsync(pIDAssetType, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpDescriptor = await this.descriptorForTypeAsync(pIDAssetType, tmpOptions.ManifestVersion);
		if (tmpDescriptor)
		{
			return new libAssetDescriptor.AssetDescriptorAccessor(tmpDescriptor, { SetKey: tmpOptions.SetKey });
		}
		return this.fallbackAccessor(tmpOptions.SetKey);
	}

	/**
	 * The compiled-in SNBI vocabulary, shaped as a descriptor accessor.
	 *
	 * This exists so that introducing the descriptor layer is provably inert: every call the dashboard
	 * used to make against SNBI-Dictionary.js resolves to the same answer through this path. It is
	 * deliberately the ONLY place left that references that module.
	 *
	 * @param {String} [pSetKey]
	 * @return {Object} an accessor over a descriptor built from SNBI-Dictionary.js
	 */
	fallbackAccessor(pSetKey)
	{
		if (!this._fallbackDescriptor)
		{
			this._fallbackDescriptor = libAssetDescriptor.normalizeDescriptor(buildDescriptorFromSNBIDictionary());
		}
		return new libAssetDescriptor.AssetDescriptorAccessor(this._fallbackDescriptor, { SetKey: pSetKey || 'SNBISubmission' });
	}
}

// Friendly names for the SNBI code-group prefixes. Lifted verbatim out of the dashboard so the
// fallback renders exactly what the dashboard used to render on its own.
const _SNBI_GROUPS =
{
	BID: 'Identification', BL: 'Location', BCL: 'Classification', BC: 'Condition', BG: 'Geometry',
	BLR: 'Load rating', BAP: 'Appraisal', BIR: 'Inspection', BRH: 'Railings', BW: 'Work',
	BE: 'Element', BCS: 'Condition state', BF: 'Feature', BH: 'Highway', BN: 'Navigation',
	BRR: 'Railroad', BIE: 'Inspection event', BSP: 'Span', BSB: 'Substructure',
	BPS: 'Posting status', BEP: 'Posting evaluation',
};
const _SNBI_GROUP_ORDER = [ 'BID', 'BL', 'BCL', 'BC', 'BG', 'BLR', 'BAP', 'BIR', 'BRH', 'BW', 'BSP', 'BSB', 'BF', 'BH', 'BN', 'BRR', 'BE', 'BCS', 'BIE', 'BPS', 'BEP' ];
// The scalar metrics the dashboard offered for map coloring, by group.
const _SNBI_METRIC_GROUPS =
{
	BC: [ 'BC01', 'BC02', 'BC03', 'BC04', 'BC05', 'BC06', 'BC07', 'BC08', 'BC09', 'BC10', 'BC11', 'BC14', 'BC15' ],
	BLR: [ 'BLR01', 'BLR02', 'BLR03', 'BLR04', 'BLR05', 'BLR06', 'BLR07', 'BLR08' ],
	BG: [ 'BG01', 'BG02', 'BG03', 'BG04', 'BG05', 'BG06', 'BG07', 'BG08', 'BG09', 'BG10', 'BG11', 'BG12', 'BG13', 'BG14', 'BG15' ],
};
// The nine collection pseudo-entries the flat dictionary carries alongside real data items.
const _SNBI_COLLECTIONS = [ 'Elements', 'Features', 'Inspections', 'PostingEvaluations', 'PostingStatuses', 'Routes', 'SpanSets', 'SubstructureSets', 'Works' ];

/**
 * Build a descriptor-shaped object from the compiled-in SNBI dictionary, so the fallback path and the
 * descriptor path are the same code.
 *
 * @return {Object} a raw (un-normalized) descriptor
 */
function buildDescriptorFromSNBIDictionary()
{
	const tmpMetricCodes = {};
	for (const tmpGroupKey of Object.keys(_SNBI_METRIC_GROUPS))
	{
		for (const tmpCode of _SNBI_METRIC_GROUPS[tmpGroupKey]) { tmpMetricCodes[tmpCode] = true; }
	}

	const tmpFields = [];
	const tmpCollections = [];
	const tmpCodes = Object.keys(libSNBI.SNBI);
	for (let i = 0; i < tmpCodes.length; i++)
	{
		const tmpCode = tmpCodes[i];
		const tmpEntry = libSNBI.SNBI[tmpCode];
		if (_SNBI_COLLECTIONS.indexOf(tmpCode) > -1)
		{
			tmpCollections.push({ Key: tmpCode, Label: tmpEntry.label, Help: tmpEntry.help, Ordinal: i, ItemFields: [] });
			continue;
		}
		// Group prefix = the leading letters of the code (BC01 -> BC). The descriptor makes this
		// explicit per field; only the fallback still has to infer it.
		const tmpGroup = String(tmpCode).replace(/\d.*$/, '');
		const tmpValueMap = libSNBI.VALUES[tmpCode];
		const tmpValues = tmpValueMap
			? Object.keys(tmpValueMap).map((pRaw, pIndex) => ({ Value: pRaw, Label: tmpValueMap[pRaw], Ordinal: pIndex, Neutral: (pRaw === 'N') }))
			: [];
		tmpFields.push(
			{
				Code: tmpCode,
				Label: tmpEntry.label,
				Group: tmpGroup,
				Ordinal: i,
				DataType: tmpValues.length ? 'enum' : (tmpEntry.units ? 'number' : 'string'),
				Units: tmpEntry.units,
				Help: tmpEntry.help,
				Explode: true,
				Metric: !!tmpMetricCodes[tmpCode],
				Format: { ValueDisplay: 'code-and-label' },
				Scale: { Kind: tmpValues.length ? 'categorical' : 'continuous', Direction: 'none', Neutral: tmpValueMap && tmpValueMap.N ? [ 'N' ] : [] },
				Values: tmpValues,
			});
	}

	return (
		{
			DescriptorVersion: libAssetDescriptor.DESCRIPTOR_VERSION,
			Status: 'published',
			AssetType: { Key: 'Bridge', Label: { Singular: 'Bridge', Plural: 'Bridges' }, Identity: { Field: 'ExternalSyncGUID', Label: 'Structure #' } },
			MetadataSets:
			[
				{
					Key: 'SNBISubmission',
					Label: 'FHWA SNBI Submission',
					Standard: 'FHWA SNBI (AASHTO, March 2022)',
					Source: 'FHWA',
					Role: 'primary',
					Shape: 'document',
					Groups: _SNBI_GROUP_ORDER.map((pKey, pIndex) => ({ Key: pKey, Label: _SNBI_GROUPS[pKey] || pKey, Ordinal: (pIndex + 1) * 10 })),
					Fields: tmpFields,
					Collections: tmpCollections,
				},
			],
			Dashboard: { DefaultSet: 'SNBISubmission', DefaultMetric: null },
		});
}

module.exports = AssetDescriptorProvider;
module.exports.buildDescriptorFromSNBIDictionary = buildDescriptorFromSNBIDictionary;
module.exports.default_configuration =
{
	ProviderIdentifier: 'AssetDescriptor',
	AutoInitialize: true,
	AutoInitializeOrdinal: 20,
};
